export function cleanObject(obj: any): any {
  if (Array.isArray(obj)) {
    return obj
      .map((v) => (v && typeof v === 'object' ? cleanObject(v) : v))
      .filter((v) => v != null); // Changed to check for both null and undefined
  } else if (obj && typeof obj === 'object') {
    return Object.entries(obj)
      .map(([k, v]) => [k, v && typeof v === 'object' ? cleanObject(v) : v])
      .reduce((a: any, [k, v]) => {
        if (
          v == null ||
          (typeof v === 'string' && v.length === 0) ||
          (Array.isArray(v) && v.length === 0)
        ) {
          return a; // Skip adding empty or null values
        }
        a[k] = v; // Add valid key-value pair
        return a;
      }, {});
  }
  return obj; // Return non-object/non-array values as-is
}

export function isEmptyObject(value: object): boolean {
  return value && Object.keys(value).length === 0 && value.constructor === Object;
}

export function objectIsNullish(obj: object): boolean {
  return Object.values(obj).every((value) => value === null);
}

export function objectToUrlParams(obj: Record<string, any>) {
  return Object.keys(obj)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
    .join('&');
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result: T = { ...target }; // Start with a shallow copy of the target object

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (typeof result[key] === 'object' && !Array.isArray(result[key])) {
          // Recursively merge nested objects
          result[key] = deepMerge(
            result[key] as T[Extract<keyof T, string>],
            source[key] as T[Extract<keyof T, string>],
          );
        } else {
          // Ensure a deep copy of the object is assigned
          result[key] = JSON.parse(JSON.stringify(source[key]));
        }
      } else {
        result[key] = source[key] as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
}
